import ReactDOM from 'react-dom/client';
import './index.css'
import App from './App.jsx'
import { Provider } from 'react-redux'
import { HelmetProvider } from 'react-helmet-async'
import { DemoViewProvider } from './contexts/DemoViewContext'
import {Store} from './Store/Store'
import { applyLangAttribute, resolveLangFromUrl, setLang } from './i18n/lang'

// A /en/ or /ta/ deep link (crawlers, shared/hreflang URLs) sets the language
// from the URL before first paint, so i18n initializes in that language.
const { urlLang } = resolveLangFromUrl();
if (urlLang) setLang(urlLang);

// Sync <html lang> with the URL/stored choice (app UI default English; SEO
// bare-root stays Hindi) before first paint. Dependency-free — i18next loads
// in lazy route chunks.
applyLangAttribute();

const root = ReactDOM.createRoot(document.getElementById('root'));

const renderApp = () =>
  root.render(
    <HelmetProvider>
      <Provider store={Store}>
        <DemoViewProvider>
          <App />
        </DemoViewProvider>
      </Provider>
    </HelmetProvider>
  );

// Web renders synchronously (the inline check below is false → no extra work,
// entry bundle untouched — we intentionally do NOT import platform.js here so it
// stays out of the landing entry chunk). The native shell first restores auth
// from durable storage (@capacitor/preferences) so a WKWebView localStorage
// eviction doesn't log the user out, then keeps it synced on background.
// tokenStore is dynamically imported so it never enters the web/entry bundle.
const isNativeApp =
  typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

// In the native app, open straight to the app — not the marketing homepage.
// Rewriting the path before render means the router mounts on /myprofile, which
// is auth-guarded: signed-in → dashboard, signed-out → /teacher-login.
if (isNativeApp && window.location.pathname === '/') {
  window.history.replaceState(null, '', '/myprofile');
}

if (isNativeApp) {
  // Restore auth from durable native storage BEFORE first render so a returning
  // user isn't briefly logged out — but NEVER let it block the app. Cap the wait
  // and always render: a stalled plugin must not leave the user stuck on the
  // boot splash. renderOnce guards against a double render.
  let rendered = false;
  const renderOnce = () => {
    if (rendered) return;
    rendered = true;
    renderApp();
  };
  import('./utils/tokenStore')
    .then(async ({ rehydrateAuthFromNative, installNativeAuthPersistence }) => {
      await Promise.race([
        rehydrateAuthFromNative(),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
      installNativeAuthPersistence();
    })
    .catch(() => {})
    .finally(renderOnce);
  // Hard safety net — render no matter what, even if the import itself stalls.
  setTimeout(renderOnce, 2000);
} else {
  renderApp();
}

// Boot Mixpanel AFTER first paint. The wrapper itself is dynamically imported
// (not statically) so neither it NOR the heavy SDK + session-replay chunk lands
// in the landing entry bundle — protects the 142 KB gz size-limit budget.
const bootAnalytics = () =>
  import('./utils/analytics').then((m) => m.initAnalytics());
if ('requestIdleCallback' in window) {
  window.requestIdleCallback(bootAnalytics, { timeout: 3000 });
} else {
  window.setTimeout(bootAnalytics, 2000);
}